import { TableSkeleton } from "components/PortalLoading";
import { portalPageMeta } from "layouts/Admin2/pageMeta";
import PageHeading from "admin/components/PageHeading";
import React, { useState } from "react";
import { FiSearch, FiBook } from "react-icons/fi";
import useBooks from "api/BookResource/useBooks";
import GeoRestrictionButton from "components/GeoRestrictionButton";
import Admin2 from "layouts/Admin2";

const GeoRestrictions = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [inputValue, setInputValue] = useState("");
	const [page, setPage] = useState(1);

	const { data: booksResponse, isLoading } = useBooks({
		page,
		searchTerm,
		rows_per_page: 20,
		from_backend: true,
	});

	const books = booksResponse?.data?.records || [];
	const pagination = booksResponse?.data;

	const handleSearch = () => {
		setSearchTerm(inputValue);
		setPage(1);
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	return (
		<Admin2 fullWidth={true}>
			<div className="w-full max-w-7xl">
				{/* Header */}
				<div className="mb-8">
					<PageHeading title="Geographic restrictions" description={portalPageMeta("/admin/geo-restrictions").description} />
				</div>

				{/* Search */}
				<div className="mb-6">
					<div className="portal-search-form max-w-md">
						<div className="relative flex-1">
							<FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
							<input
								type="text"
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Search books..."
								className="w-full pl-10  py-2 border border-gray-300 rounded-lg focus:ring-akgreener focus:border-akgreener"
							/>
						</div>
							<button
								onClick={handleSearch}
								className="portal-button portal-button-primary"
							>
								Search
							</button>
					</div>
				</div>

				{/* Books List */}
				{isLoading ? (
					<TableSkeleton label="Loading book access policies…" columns={[{ label: "Book", kind: "book" }, "Access policy", "Countries", "Actions"]} />
				) : (
					<>
						<div className="bg-white rounded-lg shadow-sm border">
							<div className="px-6 py-4 border-b border-gray-200">
								<h2 className="text-lg font-medium text-gray-900">
									Books ({pagination?.total_records || 0})
								</h2>
							</div>
							<div className="divide-y divide-gray-200">
								{books.length === 0 ? (
									<div className="px-6 py-12 text-center">
										<FiBook className="mx-auto h-12 w-12 text-gray-400" />
										<h3 className="mt-2 text-sm font-medium text-gray-900">No books found</h3>
										<p className="mt-1 text-sm text-gray-500">
											{searchTerm ? "Try adjusting your search terms." : "No books available."}
										</p>
									</div>
								) : (
									books.map((book) => <BookRow key={book.id} book={book} />)
								)}
							</div>
						</div>

						{/* Pagination */}
						{pagination && pagination.total_pages > 1 && (
							<div className="mt-6 flex items-center justify-between">
								<div className="text-sm text-gray-700">
									Showing page {pagination.current_page} of {pagination.total_pages}
								</div>
								<div className="flex space-x-2">
									<button
										onClick={() => setPage(Math.max(1, page - 1))}
										disabled={page === 1}
										className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
									>
										Previous
									</button>
									<button
										onClick={() => setPage(Math.min(pagination.total_pages, page + 1))}
										disabled={page === pagination.total_pages}
										className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
									>
										Next
									</button>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</Admin2>
	);
};

// Book Row Component
const BookRow = ({ book }) => {
	return (
		<div className="portal-geo-book-row hover:bg-gray-50">
			<div className="portal-geo-book-info">
				{/* Book Cover */}
				<div className="flex-shrink-0">
					{book.book_cover?.url ? (
						<img
							src={book.book_cover.url}
							alt={book.title}
							className="h-16 w-12 object-cover rounded"
						/>
					) : (
						<div className="h-16 w-12 bg-gray-200 rounded flex items-center justify-center">
							<FiBook className="h-6 w-6 text-gray-400" />
						</div>
					)}
				</div>

				{/* Book Info */}
				<div className="flex-1 min-w-0">
					<h3 className="text-sm font-medium text-gray-900 truncate">{book.title}</h3>
					{book.author && <p className="text-sm text-gray-500 truncate">by {book.author}</p>}
					<div className="flex flex-wrap items-center gap-2 mt-1">
						<span className="text-xs text-gray-500">ID: {book.id}</span>
						{Boolean(book.geo_restricted) && (
							<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
								Geo-Restricted
							</span>
						)}
						{!book.is_active && (
							<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
								Inactive
							</span>
						)}
					</div>
				</div>

				{/* Book Details */}
				<div className="portal-geo-book-price text-sm text-gray-500">
					{book.price && <div>${book.price}</div>}
					{book.category && <div className="text-xs">{book.category.name}</div>}
				</div>
			</div>

			{/* Actions */}
			<div className="portal-geo-book-action">
				<GeoRestrictionButton book={book} variant="button" />
			</div>
		</div>
	);
};

export default GeoRestrictions;
